import { useMemo, useState } from "react";
import {
  CalendarOutlined,
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Calendar,
  Card,
  DatePicker,
  Empty,
  List,
  Modal,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";

import useAdaptiveCalendarLayout from "../../hooks/useAdaptiveCalendarLayout";
import {
  CRITICALITY_COLORS,
  CRITICALITY_LABELS,
  formatDate,
  formatLabel,
  formatOfficeName,
  formatSubOfficeName,
  STATUS_COLORS,
} from "./prismSubscriptionUtils";
import {
  buildCalendarDateItems,
  buildCalendarEvents,
  getCalendarDateCellSummary,
  getCalendarWeekCount,
  getCalendarEventTypeClass,
  getCalendarEventTypeLabel,
  getNoteColorClassName,
  groupCalendarEventsByDate,
  groupCalendarNotesByDate,
  setCalendarMonthAndYear,
  sortCalendarEvents,
} from "./prismCalendarUtils";

const { Text } = Typography;

const PrismDashboardCalendarPreview = ({
  subscriptions = [],
  details = {},
  notes = [],
  loading = false,
  noteError = null,
}) => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const calendarWeekCount = getCalendarWeekCount(currentDate);
  const {
    calendarContainerRef,
    density: calendarDensity,
    visibleItemLimit,
  } = useAdaptiveCalendarLayout(calendarWeekCount);
  const calendarEvents = useMemo(
    () => buildCalendarEvents(subscriptions, details),
    [subscriptions, details],
  );
  const calendarEventsByDate = useMemo(
    () => groupCalendarEventsByDate(calendarEvents),
    [calendarEvents],
  );
  const calendarNotesByDate = useMemo(
    () => groupCalendarNotesByDate(notes),
    [notes],
  );
  const selectedDateKey = selectedDate?.format("YYYY-MM-DD") || null;
  const selectedDateItems = useMemo(() => {
    if (!selectedDateKey) return [];

    const selectedDateEvents = sortCalendarEvents(
      calendarEventsByDate[selectedDateKey] || [],
    );
    const selectedDateNotes = calendarNotesByDate[selectedDateKey] || [];

    return buildCalendarDateItems(selectedDateNotes, selectedDateEvents);
  }, [calendarEventsByDate, calendarNotesByDate, selectedDateKey]);

  const renderDateItem = (item) => {
    if (item.kind === "note") {
      const colorClass = getNoteColorClassName(item.note.color);
      const tooltipText = `Note: ${item.note.title}`;

      return (
        <Tooltip key={item.key} title={tooltipText}>
          <div
            aria-label={tooltipText}
            className={`prism-dashboard-calendar-entry prism-dashboard-calendar-entry--note ${colorClass}`}
          >
            <span className="prism-dashboard-calendar-entry__type">Note</span>
            <span className="prism-dashboard-calendar-entry__name">
              {item.note.title}
            </span>
          </div>
        </Tooltip>
      );
    }

    const event = item.event;
    const subscriptionName = event.subscription.subscription_name || "-";
    const typeClass = getCalendarEventTypeClass(event.types);
    const typeLabel = getCalendarEventTypeLabel(event.types);
    const shortTypeLabel = getCalendarEventTypeLabel(event.types, true);
    const tooltipText = `${typeLabel}: ${subscriptionName}`;

    return (
      <Tooltip key={item.key} title={tooltipText}>
        <div
          aria-label={tooltipText}
          className={`prism-dashboard-calendar-entry prism-dashboard-calendar-entry--${typeClass}`}
        >
          <span className="prism-dashboard-calendar-entry__type">
            {shortTypeLabel}
          </span>
          <span className="prism-dashboard-calendar-entry__name">
            {subscriptionName}
          </span>
        </div>
      </Tooltip>
    );
  };

  const renderDateCell = (date) => {
    const dateKey = date.format("YYYY-MM-DD");
    const events = sortCalendarEvents(calendarEventsByDate[dateKey] || []);
    const notesForDate = calendarNotesByDate[dateKey] || [];
    const { items, visibleItems, hiddenItemCount } =
      getCalendarDateCellSummary(notesForDate, events, visibleItemLimit);

    if (items.length === 0) return null;

    return (
      <div className="prism-dashboard-calendar-date-items">
        {visibleItems.map(renderDateItem)}
        {hiddenItemCount > 0 && (
          <Text className="prism-dashboard-calendar-date-items__more">
            +{hiddenItemCount} more
          </Text>
        )}
      </div>
    );
  };

  const renderCalendarCell = (date, info) => {
    if (info.type === "date") return renderDateCell(date);
    return info.originNode;
  };

  const handleDateSelect = (date) => {
    setCurrentDate(date);
    setSelectedDate(date);
    setIsDayModalOpen(true);
  };

  const renderSelectedDateItem = (item) => {
    if (item.kind === "note") {
      const colorClass = getNoteColorClassName(item.note.color);

      return (
        <List.Item key={item.key}>
          <div
            className={`prism-dashboard-day-item prism-dashboard-day-item--note ${colorClass}`}
          >
            <div className="prism-dashboard-day-item__tags">
              <Tag className={`prism-dashboard-day-note-tag ${colorClass}`}>
                <FileTextOutlined /> Personal Note
              </Tag>
            </div>
            <Text strong className="prism-dashboard-day-item__name">
              {item.note.title}
            </Text>
            {item.note.note && (
              <Text className="prism-dashboard-day-item__body">
                {item.note.note}
              </Text>
            )}
          </div>
        </List.Item>
      );
    }

    const event = item.event;
    const typeClass = getCalendarEventTypeClass(event.types);
    const subscription = event.subscription;
    const eventTypeColor =
      typeClass === "combined"
        ? "purple"
        : typeClass === "end"
          ? "orange"
          : "blue";

    return (
      <List.Item key={item.key}>
        <div
          className={`prism-dashboard-day-item prism-dashboard-day-item--${typeClass}`}
        >
          <Space
            className="prism-dashboard-day-item__tags"
            size={[5, 5]}
            wrap
          >
            <Tag color={eventTypeColor}>
              {getCalendarEventTypeLabel(event.types)}
            </Tag>
            {subscription.contract_status && (
              <Tag color={STATUS_COLORS[subscription.contract_status] || "blue"}>
                {formatLabel(subscription.contract_status)}
              </Tag>
            )}
            {event.criticality && (
              <Tag color={CRITICALITY_COLORS[event.criticality] || "default"}>
                {CRITICALITY_LABELS[event.criticality] ||
                  formatLabel(event.criticality)}
              </Tag>
            )}
          </Space>
          <Text strong className="prism-dashboard-day-item__name">
            {subscription.subscription_name || "-"}
          </Text>
          {subscription.subscription_details && (
            <Text className="prism-dashboard-day-item__description" type="secondary">
              {subscription.subscription_details}
            </Text>
          )}
          <div className="prism-dashboard-day-item__details">
            <div>
              <Text type="secondary">Event date</Text>
              <Text>{formatDate(event.dateKey)}</Text>
            </div>
            <div>
              <Text type="secondary">Office</Text>
              <Text>{formatOfficeName(subscription.subscriber_department)}</Text>
            </div>
            <div>
              <Text type="secondary">Unit</Text>
              <Text>
                {formatSubOfficeName(subscription.subscriber_sub_department)}
              </Text>
            </div>
            <div>
              <Text type="secondary">Renewal frequency</Text>
              <Text>{formatLabel(subscription.contract_renewal_frequency)}</Text>
            </div>
          </div>
        </div>
      </List.Item>
    );
  };

  const itemCountLabel = `${selectedDateItems.length} item${
    selectedDateItems.length === 1 ? "" : "s"
  }`;
  const selectedEventCount = selectedDateItems.filter(
    (item) => item.kind === "event",
  ).length;
  const selectedNoteCount = selectedDateItems.length - selectedEventCount;

  return (
    <>
      <Card className="prism-dashboard-calendar-card" size="small">
        <div className="prism-dashboard-calendar-toolbar">
          <Space size={6}>
            <Button
              aria-label="Previous month"
              icon={<LeftOutlined />}
              onClick={() =>
                setCurrentDate((current) => current.subtract(1, "month"))
              }
              size="small"
            />
            <Button onClick={() => setCurrentDate(dayjs())} size="small">
              Today
            </Button>
            <Button
              aria-label="Next month"
              icon={<RightOutlined />}
              onClick={() =>
                setCurrentDate((current) => current.add(1, "month"))
              }
              size="small"
            />
          </Space>
          <DatePicker
            allowClear={false}
            aria-label="Select dashboard calendar month and year"
            className="prism-dashboard-calendar-period-picker"
            format="MMMM YYYY"
            onChange={(selectedMonth) =>
              setCurrentDate((current) =>
                setCalendarMonthAndYear(current, selectedMonth),
              )
            }
            picker="month"
            size="small"
            value={currentDate}
          />
        </div>

        {noteError && (
          <Alert
            className="prism-dashboard-calendar-note-alert"
            message="Personal notes are temporarily unavailable."
            showIcon
            type="warning"
          />
        )}

        <Spin className="prism-dashboard-calendar-spin" spinning={loading}>
          <div
            className={`prism-dashboard-calendar-grid prism-calendar-density--${calendarDensity} prism-calendar-weeks--${calendarWeekCount}`}
            data-calendar-density={calendarDensity}
            ref={calendarContainerRef}
          >
            <Calendar
              cellRender={renderCalendarCell}
              className="prism-dashboard-calendar"
              headerRender={() => null}
              onPanelChange={(value) => setCurrentDate(value)}
              onSelect={handleDateSelect}
              value={currentDate}
            />
          </div>
        </Spin>
      </Card>

      <Modal
        className="prism-dashboard-day-modal"
        footer={
          <Button onClick={() => setIsDayModalOpen(false)}>Close</Button>
        }
        onCancel={() => setIsDayModalOpen(false)}
        open={isDayModalOpen}
        title={
          <Space className="prism-dashboard-day-modal__title" size={8} wrap>
            <CalendarOutlined />
            <span>
              {selectedDate?.format("MMMM D, YYYY") || "Selected date"}
            </span>
            <Tag>{itemCountLabel}</Tag>
          </Space>
        }
        width={720}
      >
        {selectedDateItems.length === 0 ? (
          <Empty
            description="No subscription events or notes for this date."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <>
            <div className="prism-dashboard-day-summary">
              <div>
                <Text strong>Daily schedule</Text>
                <Text type="secondary">
                  Complete activity for the selected date
                </Text>
              </div>
              <Space size={[5, 5]} wrap>
                <Tag color="blue">
                  {selectedEventCount} subscription event
                  {selectedEventCount === 1 ? "" : "s"}
                </Tag>
                <Tag color="purple">
                  {selectedNoteCount} personal note
                  {selectedNoteCount === 1 ? "" : "s"}
                </Tag>
              </Space>
            </div>
            <List
              className="prism-dashboard-day-list"
              dataSource={selectedDateItems}
              renderItem={renderSelectedDateItem}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default PrismDashboardCalendarPreview;
